import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";

// --- Helper Component for Chips ---
const SuggestionChip = ({ label, onClick, mode }) => (
  <Chip
    label={label}
    onClick={onClick}
    size="small"
    sx={{
      borderRadius: "8px",
      bgcolor: mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
      border: "1px solid",
      borderColor: "divider",
      color: "text.primary",
      fontSize: "0.7rem",
      "&:hover": {
        bgcolor: mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      },
    }}
  />
);

const ChatInput = ({ onSendMessage, mode, disabled }) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const theme = useTheme();

  const currentMode = mode || theme.palette.mode;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = (textOverride) => {
    const textToSend =
      typeof textOverride === "string" ? textOverride : inputValue;

    if (textToSend.trim() || selectedFile) {
      onSendMessage(textToSend, selectedFile);

      setInputValue("");
      handleRemoveFile();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        p: 0.5,
        borderTop: "0.5px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <input
        type="file"
        hidden
        ref={fileInputRef}
        onChange={handleFileSelect}
      />

      {selectedFile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor:
              currentMode === "dark" ? "rgba(91, 150, 247, 0.1)" : "#f0f7ff",
            border: "1px dashed #f32525ff",
            borderRadius: "8px",
            p: 0.75,
            mb: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                p: 0.5,
                bgcolor: "background.paper",
                borderRadius: "4px",
                display: "flex",
              }}
            >
              <InsertDriveFileRoundedIcon
                fontSize="small"
                sx={{ color: "#5b96f7" }}
              />
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: "text.primary", fontSize: "0.8rem" }}
              >
                {selectedFile.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem" }}
              >
                {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={handleRemoveFile}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={selectedFile ? "Add a message..." : "Message MOBY..."}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              backgroundColor: "background.paper",
              pl: 2,
              pr: 1,
              padding: "5px",
              paddingLeft: "10px",
              "& fieldset": { borderColor: "divider" },
              "&:hover fieldset": { borderColor: "text.secondary" },
              "&.Mui-focused fieldset": {
                borderColor: "#eb2c2cff",
                borderWidth: "1px",
              },
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
              fontFamily: "Arial, serif",
              fontSize: "0.75rem",
              paddingLeft: "7px",
              "&::placeholder": {
                fontSize: "0.75rem",
                opacity: 0.7,
              },
            },
          }}
        />

        <Box sx={{ mt: 0 }} onClick={() => handleSend()}>
          <IconButton
            disabled={(!inputValue.trim() && !selectedFile) || disabled}
            sx={{
              bgcolor:
                inputValue.trim() || selectedFile
                  ? currentMode === "dark"
                    ? "#1e1e1e"
                    : "white"
                  : "action.disabledBackground",
              color: "#f32525ff",
              width: "35px",
              height: "35px",
              transition: "0.2s",
              border:
                currentMode === "dark" ? "1px solid #ff4d4d" : "1px solid red",
              "&:hover": {
                cursor: "pointer",
                bgcolor: currentMode === "dark" ? "#2d2d2d" : "#f5f5f5",
              },
              "&.Mui-disabled": {
                bgcolor: "action.disabledBackground",
                color: "text.disabled",
                border: "1px solid transparent",
              },
            }}
          >
            <SendRoundedIcon
              fontSize="small"
              sx={{
                transform: "rotate(-45deg)",
                marginLeft: "1.5px",
                mt: -0.7,
              }}
            />
          </IconButton>
        </Box>
      </Stack>

      {!selectedFile && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 0.8,
            overflowX: "auto",
            p: 0,
            color: "text.primary",
            fontFamily: "Arial, serif",
            fontSize: "0.65rem",
            opacity: 0.7,
            pb: 0.5,
            "&::-webkit-scrollbar": { height: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "6px",
            },
          }}
        >
          <SuggestionChip
            label="Check Inventory"
            onClick={() => handleSend("Check Inventory")}
            mode={currentMode}
          />
          <SuggestionChip
            label="Production Status"
            onClick={() => handleSend("What is the Production Status?")}
            mode={currentMode}
          />
        </Stack>
      )}
    </Box>
  );
};

export default ChatInput;
